import { useState } from 'react';
import { apiFetch, apiUploadFile } from '../utils/api';
import { DEFAULT_TRACK_FORM } from '../utils/rescuePush';

/**
 * 轨迹表单、文件预览解析、推送
 */
export default function useRescuePushTrack({
  clearTrackPreviewRef,
  showTrackPreviewRef,
  displayTrackOnMapRef
}) {
  const [trackForm, setTrackForm] = useState({ ...DEFAULT_TRACK_FORM });
  const [trackFile, setTrackFile] = useState(null);
  const [trackPreviewCoords, setTrackPreviewCoords] = useState(null);
  const [trackPreviewLoading, setTrackPreviewLoading] = useState(false);
  const [trackPreviewInfo, setTrackPreviewInfo] = useState('');
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackResult, setTrackResult] = useState(null);

  const handleTrackFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    setTrackResult(null);
    clearTrackPreviewRef.current();

    if (!file) {
      setTrackFile(null);
      return;
    }

    setTrackFile(file);
    setTrackPreviewLoading(true);
    setTrackPreviewInfo('正在解析轨迹文件...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await apiUploadFile('/api/parse-file', formData);
      const uploadText = await uploadResponse.text();
      let uploadData;
      try {
        uploadData = JSON.parse(uploadText);
      } catch (err) {
        setTrackFile(null);
        setTrackPreviewInfo('');
        setTrackResult({
          success: false,
          msg: `文件解析接口异常（HTTP ${uploadResponse.status}）：${uploadText.slice(0, 120)}`
        });
        return;
      }

      if (!uploadResponse.ok || !uploadData.success || !uploadData.coordinates || uploadData.coordinates.length < 2) {
        setTrackFile(null);
        setTrackPreviewCoords(null);
        setTrackPreviewInfo('');
        setTrackResult({
          success: false,
          msg: uploadData.error || uploadData.detail || `文件解析失败（HTTP ${uploadResponse.status}）`
        });
        e.target.value = '';
        return;
      }

      showTrackPreviewRef.current(uploadData.coordinates, file.name);
      setTrackResult({
        success: true,
        msg: `已解析 ${uploadData.coordinates.length} 个点，请在右侧地图确认预览后点击「发送轨迹」`
      });
    } catch (err) {
      console.error('轨迹预览失败:', err);
      setTrackFile(null);
      clearTrackPreviewRef.current();
      setTrackResult({ success: false, msg: '预览失败: ' + err.message });
      e.target.value = '';
    } finally {
      setTrackPreviewLoading(false);
    }
  };

  const handleTrackPush = async () => {
    if (!trackFile && !(trackPreviewCoords && trackPreviewCoords.length >= 2)) {
      setTrackResult({ success: false, msg: '请先选择并预览轨迹文件' });
      return;
    }
    setTrackLoading(true);
    setTrackResult(null);
    try {
      let coordinates = trackPreviewCoords;
      let fileType = (trackFile && trackFile.name.split('.').pop()) || 'kml';

      if (!coordinates || coordinates.length < 2) {
        if (!trackFile) {
          setTrackResult({ success: false, msg: '请重新选择轨迹文件' });
          setTrackLoading(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', trackFile);
        const uploadResponse = await apiUploadFile('/api/parse-file', formData);
        const uploadText = await uploadResponse.text();
        let uploadData;
        try {
          uploadData = JSON.parse(uploadText);
        } catch (parseErr) {
          console.error('解析文件接口返回非 JSON:', uploadText.slice(0, 200));
          setTrackResult({
            success: false,
            msg: `文件解析接口异常（HTTP ${uploadResponse.status}）：${uploadText.slice(0, 120)}`
          });
          setTrackLoading(false);
          return;
        }

        if (!uploadData.success || !uploadData.coordinates || uploadData.coordinates.length < 2) {
          setTrackResult({
            success: false,
            msg: uploadData.error || '文件中没有找到有效的坐标数据'
          });
          setTrackLoading(false);
          return;
        }
        coordinates = uploadData.coordinates;
        fileType = uploadData.type || fileType;
      }

      console.log('推送轨迹坐标点数:', coordinates.length);

      const pushData = {
        ...trackForm,
        coordinates,
        file_type: String(fileType).toLowerCase(),
        file_name: trackFile ? trackFile.name : 'preview_track.kml',
        org_names: trackForm.to_all ? [] : trackForm.org_names,
        group_names: trackForm.to_all ? [] : trackForm.group_names,
        user_names: trackForm.to_all ? [] : trackForm.user_names
      };

      const response = await apiFetch('/api/rescue/track', {
        method: 'POST',
        body: JSON.stringify(pushData)
      });
      const raw = await response.text();
      let data = {};
      try {
        data = JSON.parse(raw);
      } catch (_) {
        setTrackResult({
          success: false,
          msg: `轨迹推送失败（HTTP ${response.status}）：服务器返回非 JSON`
        });
        return;
      }
      if (response.ok && data.success) {
        clearTrackPreviewRef.current();
        setTrackResult({ success: true, msg: '轨迹推送成功，已同步到指挥系统', data: data.track });

        if (data.track) {
          displayTrackOnMapRef.current(data.track);
        }

        setTrackFile(null);
        setTrackForm((f) => ({
          ...f,
          message: ''
        }));
      } else {
        const permHint = data.requiredPermission
          ? `（需要权限「${data.requiredPermission}」，请在管理后台给当前用户分组勾选该权限后重新登录）`
          : '';
        setTrackResult({
          success: false,
          msg:
            (data.error || `轨迹推送失败（HTTP ${response.status}）`) +
            (data.detail ? `：${data.detail}` : '') +
            permHint
        });
      }
    } catch (e) {
      console.error('轨迹推送失败:', e);
      setTrackResult({ success: false, msg: '推送失败: ' + e.message });
    } finally {
      setTrackLoading(false);
    }
  };

  return {
    trackForm,
    setTrackForm,
    trackFile,
    setTrackFile,
    trackPreviewCoords,
    setTrackPreviewCoords,
    trackPreviewLoading,
    trackPreviewInfo,
    setTrackPreviewInfo,
    trackLoading,
    trackResult,
    handleTrackFileChange,
    handleTrackPush
  };
}
